import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import type { TenantInfo } from '../page';

interface Props {
  tenant: TenantInfo;
  onTenantChange: (t: TenantInfo) => void;
}

const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const DOMAIN_STATUS_STYLES: Record<string, { bg: string; text: string; label: string; icon: string }> = {
  active:      { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Activo',           icon: 'ri-checkbox-circle-line' },
  pending_dns: { bg: 'bg-sky-50',     text: 'text-sky-700',     label: 'DNS Pendiente',     icon: 'ri-time-line' },
  error:       { bg: 'bg-red-50',     text: 'text-red-600',     label: 'Error',             icon: 'ri-close-circle-line' },
  none:        { bg: 'bg-slate-100',  text: 'text-slate-500',   label: 'Sin configurar',    icon: 'ri-global-line' },
  disabled:    { bg: 'bg-slate-100',  text: 'text-slate-500',   label: 'Desactivado',       icon: 'ri-eye-off-line' },
};

export default function DomainTab({ tenant, onTenantChange }: Props) {

  const handleToggleCustomDomain = async () => {
    const newEnabled = !tenant.custom_domain_enabled;
    const { error } = await supabase.from('saas_tenants').update({
      custom_domain_enabled: newEnabled,
      updated_at: new Date().toISOString(),
    }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, custom_domain_enabled: newEnabled });
    Swal.fire({
      title: newEnabled ? 'Custom domain habilitado' : 'Custom domain desactivado',
      icon: 'success', timer: 1200, showConfirmButton: false, ...SW,
    });
  };

  const handleChangeDomainStatus = async () => {
    const opts: Record<string, string> = {
      none:        'Sin configurar',
      pending_dns: 'DNS Pendiente',
      active:      'Activo',
      error:       'Error',
      disabled:    'Desactivado',
    };
    const { value: newStatus } = await Swal.fire({
      title: 'Cambiar status del dominio',
      input: 'select', inputOptions: opts,
      inputValue: tenant.custom_domain_status || 'none',
      showCancelButton: true, confirmButtonText: 'Aplicar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newStatus || newStatus === tenant.custom_domain_status) return;
    const { error } = await supabase.from('saas_tenants').update({
      custom_domain_status: newStatus, updated_at: new Date().toISOString(),
    }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, custom_domain_status: newStatus });
    Swal.fire({ title: 'Status actualizado', icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
  };

  const handleSetCustomDomain = async () => {
    const { value: newDomain } = await Swal.fire({
      title: 'Configurar custom domain',
      input: 'text',
      inputValue: tenant.custom_domain ?? '',
      inputPlaceholder: 'fotografia.com o estudio.com',
      html: `<p style="color:#64748b;font-size:13px">Ingresa el dominio sin https://</p>`,
      showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (newDomain === undefined || newDomain === null) return;
    const clean = newDomain.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    const { error } = await supabase.from('saas_tenants').update({
      custom_domain: clean || null,
      custom_domain_status: clean ? 'pending_dns' : 'none',
      custom_domain_enabled: !!clean,
      updated_at: new Date().toISOString(),
    }).eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, custom_domain: clean || null, custom_domain_status: clean ? 'pending_dns' : 'none', custom_domain_enabled: !!clean });
    Swal.fire({ title: clean ? `Dominio configurado: ${clean}` : 'Dominio eliminado', icon: 'success', timer: 1400, showConfirmButton: false, ...SW });
  };

  const domStatus = DOMAIN_STATUS_STYLES[tenant.custom_domain_status || 'none'] ?? DOMAIN_STATUS_STYLES.none;

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Tenant URL (always exists) */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <i className="ri-link text-base"></i>
          </span>
          <div>
            <p className="text-slate-800 text-sm font-semibold">URL del tenant</p>
            <p className="text-slate-400 text-xs">Página pública del estudio en la plataforma</p>
          </div>
          <span className="ml-auto px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
            Activo
          </span>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-slate-600 text-sm font-mono">/s/{tenant.slug}</span>
          <div className="flex items-center gap-2">
            <a
              href={`/s/${tenant.slug}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              Abrir
            </a>
          </div>
        </div>
      </div>

      {/* Custom domain */}
      <div className={`bg-white border rounded-2xl p-5 ${tenant.custom_domain ? 'border-slate-200' : 'border-dashed border-slate-300'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className={`w-9 h-9 flex items-center justify-center rounded-xl ${tenant.custom_domain ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
              <i className="ri-global-line text-base"></i>
            </span>
            <div>
              <p className="text-slate-800 text-sm font-semibold">Custom Domain</p>
              <p className="text-slate-400 text-xs">Dominio personalizado del estudio</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tenant.custom_domain && (
              <button
                onClick={handleToggleCustomDomain}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${tenant.custom_domain_enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
                title={tenant.custom_domain_enabled ? 'Desactivar' : 'Activar'}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${tenant.custom_domain_enabled ? 'left-5' : 'left-0.5'}`} />
              </button>
            )}
            <button
              onClick={handleSetCustomDomain}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-pencil-line"></i>
              {tenant.custom_domain ? 'Editar' : 'Configurar'}
            </button>
          </div>
        </div>

        {tenant.custom_domain ? (
          <>
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between mb-3">
              <span className="text-slate-700 text-sm font-mono">{tenant.custom_domain}</span>
              <div className="flex items-center gap-2">
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${domStatus.bg} ${domStatus.text}`}>
                  <i className={`${domStatus.icon} text-xs`}></i>
                  {domStatus.label}
                </span>
                <button
                  onClick={handleChangeDomainStatus}
                  className="px-2.5 py-1 text-xs text-slate-400 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
                  title="Cambiar status"
                >
                  <i className="ri-refresh-line"></i>
                </button>
              </div>
            </div>

            {/* DNS instructions */}
            {tenant.custom_domain_status === 'pending_dns' && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <p className="text-sky-700 text-xs font-semibold mb-2">
                  <i className="ri-information-line mr-1.5"></i>
                  Instrucciones de DNS
                </p>
                <p className="text-sky-600 text-xs mb-3">El cliente debe agregar el siguiente registro CNAME en su proveedor de DNS:</p>
                <div className="bg-white border border-sky-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-3 gap-0 text-xs">
                    {[
                      { label: 'Tipo', value: 'CNAME' },
                      { label: 'Host', value: tenant.custom_domain },
                      { label: 'Apunta a', value: 'proxy.pixory.ai' },
                    ].map((row, i) => (
                      <div key={row.label} className={`px-3 py-2 ${i < 2 ? 'border-r border-sky-100' : ''}`}>
                        <p className="text-slate-400 text-[10px] uppercase mb-1">{row.label}</p>
                        <p className="text-slate-700 font-mono text-xs break-all">{row.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-sky-500 text-[10px] mt-2">La propagación de DNS puede tomar hasta 24–48 horas.</p>
              </div>
            )}

            {tenant.custom_domain_status === 'active' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                <p className="text-emerald-700 text-xs">Dominio configurado y activo. El tenant puede acceder vía su dominio personalizado.</p>
              </div>
            )}

            {tenant.custom_domain_status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                <i className="ri-close-circle-fill text-red-500"></i>
                <p className="text-red-700 text-xs">Error de configuración DNS. Verificar registros y reintentar.</p>
              </div>
            )}
          </>
        ) : (
          <div className="py-6 text-center">
            <p className="text-slate-400 text-sm mb-3">Sin custom domain configurado</p>
            <button
              onClick={handleSetCustomDomain}
              className="px-4 py-2 text-sm bg-[#2563eb] hover:bg-blue-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Configurar dominio
            </button>
          </div>
        )}
      </div>

      {/* Slug edit */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Slug del tenant</p>
            <p className="text-slate-400 text-xs mt-0.5">Identificador único en la plataforma</p>
          </div>
          <button
            onClick={async () => {
              const { value: newSlug } = await Swal.fire({
                title: 'Editar slug',
                input: 'text', inputValue: tenant.slug,
                inputPlaceholder: 'mi-estudio', showCancelButton: true,
                confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
              });
              if (!newSlug || newSlug === tenant.slug) return;
              const clean = newSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
              const { error } = await supabase.from('saas_tenants').update({ slug: clean, updated_at: new Date().toISOString() }).eq('id', tenant.id);
              if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
              onTenantChange({ ...tenant, slug: clean });
              Swal.fire({ title: `Slug → ${clean}`, icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
            }}
            className="text-xs text-slate-400 hover:text-blue-600 cursor-pointer transition-colors"
          >
            <i className="ri-pencil-line mr-1"></i>Editar
          </button>
        </div>
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5">
          <span className="text-slate-400 text-xs font-mono">/s/</span>
          <span className="text-slate-800 text-sm font-mono font-semibold">{tenant.slug}</span>
        </div>
      </div>
    </div>
  );
}
